import HTML5Backend from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { DndProvider } from 'react-dnd';
import {
  Card,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import { Info } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { grayColor } from 'assets/jss/material-dashboard-pro-react';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedGridStyle';
import { sortByKey } from 'helpers/helpers';
import CardBody from 'components/Card/CardBody';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';
import FormControlButtons from 'components/FormControlButtons/FormControlButtons';
import FormImageUpload from 'components/FormImageUpload/FormImageUpload';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import FormTextInputTranslatable from 'components/FormTextInput/FormTextInputTranslatable';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import SelectInput from 'components/FormSelect/SelectInput';
import AdminTable from 'layouts/AdminTable';

import SuperVariantBetaPricesTable from '../components/SuperVariantBetaPricesTable';
import DraggableTable from '../DraggableTable';
import './styles.css';

const useStyles = makeStyles({
  labelHorizontal: extendedFormsStyle.labelHorizontal,
  label: {
    display: 'flex',
  },
  tooltipWrapper: {
    marginLeft: '10px',
  },
  infoIcon: {
    color: grayColor[0],
  },
  imageFormContainer: { marginTop: '20px' },
  tableLayout: { tableLayout: 'fixed' },
  variantWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
  variantAddRow: { display: 'block' },
  superVariantWrapper: { marginTop: '25px' },
  minMaxValueWrapper: {
    justifyContent: 'end',
  },
  valueConfiguration: { textAlign: 'center' },
  formControlButton: { marginTop: '25px' },
});

const FormVariantsView = ({
  state,
  setState,
  isEdit,
  setCalories,
  handleNameChange,
  handleVariantName,
  handleVariantWorkName,
  toggleCalorificActivness,
  removeRow,
  handleShortNameChange,
  handlePositionChange,
  handleChangeIntField,
  handleDiets,
  toggleCheckboxState,
  handleToggle,
  handleMealTypes,
  handleDescriptionChange,
  handleInternalPositionChange,
  addCalorie,
  getSizeName,
  getImage,
  removeImage,
  getRowSum,
  openModal,
  handleSubmit,
  mealTypes,
  variant,
  diets,
  multinational,
}) => {
  const classes = useStyles();

  const { t } = useTranslation();

  return (
    <AdminTable
      title={isEdit ? t('form.editVariant') : t('form.addNewVariant')}
    >
      {state.modal}
      <GridContainer>
        <GridItem sm={4}>
          <FormTextInputTranslatable
            label={`${t('form.field.variantName')}*`}
            name="variant.name"
            value={state.variant.name}
            handleChange={handleNameChange}
          />
        </GridItem>
        <GridItem sm={1}>
          <FormTextInput
            label={`${t('columns.code')}*`}
            name="variant.shortName"
            value={state.variant.shortName}
            handleChange={handleShortNameChange}
            inputSize={12}
            maxLength={32}
          />
        </GridItem>
        <GridItem sm={1}>
          <FormTextInput
            label={`${t('form.field.order')}*`}
            name="variant.position"
            type="number"
            value={state.variant.position}
            handleChange={handlePositionChange}
            inputSize={12}
            maxLength={6}
            inputProps={{ min: 0 }}
          />
        </GridItem>
        <GridItem sm={3}>
          <FormTextInput
            label={`${t('form.field.internalOrder')}*`}
            name="internalPosition"
            type="number"
            value={state.variant.internalPosition}
            handleChange={handleInternalPositionChange}
            inputSize={12}
            maxLength={6}
            inputProps={{ min: 0 }}
          />
        </GridItem>
        <GridItem sm={3} />
        <GridItem sm={5}>
          <FormTextInputTranslatable
            label={t('form.field.description')}
            name="description"
            value={state.variant.description || ''}
            handleChange={handleDescriptionChange}
            isRequired={false}
          />
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={6} lg={3}>
          <LabeledCheckbox
            label={
              <div className={classes.label}>
                {t('form.field.active.label')}{' '}
                <div className={classes.tooltipWrapper}>
                  <Tooltip
                    title={
                      <>
                        <p>{t('form.variantActiveInfo1')}:</p>
                        <ul>
                          <li>{t('form.variantActiveInfo2')}</li>
                          <li>{t('form.variantActiveInfo3')}</li>
                        </ul>
                      </>
                    }
                    placement="right"
                  >
                    <Info className={classes.infoIcon} fontSize="small" />
                  </Tooltip>
                </div>
              </div>
            }
            checked={state.variant.active}
            onChange={() => {
              toggleCheckboxState('active');
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3}>
          <LabeledCheckbox
            onClick={() => {
              toggleCheckboxState('allowSelectMenuFromOtherVariants');
            }}
            checked={state.variant.allowSelectMenuFromOtherVariants}
            label={
              <div className={classes.label}>
                {t('form.field.allowSelectMenuFromOtherVariants.label')}{' '}
                <div className={classes.tooltipWrapper}>
                  <Tooltip
                    title={
                      <>
                        <p>
                          {t(
                            'form.variantAllowSelectMenuFromOtherVariantsInfo1'
                          )}
                          :
                        </p>
                        <ul>
                          <li>
                            {t(
                              'form.variantAllowSelectMenuFromOtherVariantsInfo2'
                            )}
                          </li>
                        </ul>
                      </>
                    }
                    placement="right"
                  >
                    <Info className={classes.infoIcon} fontSize="small" />
                  </Tooltip>
                </div>
              </div>
            }
          />
        </GridItem>
      </GridContainer>
      <SelectInput
        multiple={true}
        label={t('form.field.avDiets')}
        mapBy="name"
        trackBy="@id"
        value={state.variant.diets}
        options={diets}
        handleChange={(e, obj) => {
          handleDiets(e, obj);
        }}
      />
      <SelectInput
        multiple={true}
        label={`${t('form.field.mealTypes')}*`}
        mapBy="name"
        trackBy="@id"
        value={state.variant.mealTypes}
        options={sortByKey(mealTypes, 'position')}
        handleChange={handleMealTypes}
        id="types"
        size={12}
      />
      <GridContainer className={classes.imageFormContainer}>
        <GridItem xs={12} sm={2}>
          <FormLabel className={classes.labelHorizontal}>
            {t('form.field.variantPhoto')}
          </FormLabel>
        </GridItem>
        <GridItem xs={12} sm={10}>
          <FormImageUpload
            stateName="image"
            getImage={getImage}
            removeImage={removeImage}
            previewUrl={state.imageUrl}
          />
        </GridItem>
      </GridContainer>
      <Table className={classes.tableLayout}>
        {state.variant?.mealTypes?.length > 0 && (
          <TableHead>
            <TableRow>
              <TableCell align="left">{t('form.field.name')} *</TableCell>
              <TableCell align="left">{t('form.field.workingName')}</TableCell>
              {state.variant.mealTypes.map(mealType => (
                <TableCell key={mealType.id} align="center" padding="none">
                  {mealType.name} *
                </TableCell>
              ))}
              <TableCell align="left">{t('diets.active')}</TableCell>
              <TableCell align="left">{t('form.field.caloriesSum')}</TableCell>
            </TableRow>
          </TableHead>
        )}
      </Table>
      <DndProvider backend={HTML5Backend}>
        <DraggableTable
          calories={state.variant.calories}
          variant={state.variant}
          setCalories={setCalories}
          getSizeName={getSizeName}
          getRowSum={getRowSum}
          openModal={openModal}
          handleVariantName={handleVariantName}
          handleVariantWorkName={handleVariantWorkName}
          removeRow={removeRow}
          toggleCalorificActivness={toggleCalorificActivness}
        />
      </DndProvider>
      <Table>
        <TableBody>
          {state.variant.mealTypes.length > 0 && (
            <TableRow>
              <TableCell
                className="variant-addRow"
                colSpan={variant?.mealTypes?.length + 2}
                onClick={addCalorie}
              >
                <div className={classes.variantWrapper}>
                  <span className={classes.variantAddRow}>
                    + {t('form.addCalories')}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className={classes.superVariantWrapper}>
        <Card>
          <CardBody>
            <GridContainer>
              <GridItem xs={12} sm={6} lg={3}>
                <LabeledCheckbox
                  onClick={() => handleToggle('superVariantBeta')}
                  checked={state.variant.superVariantBeta}
                  label={
                    <div className={classes.label}>
                      {t('form.field.superVariantBeta.label')}{' '}
                    </div>
                  }
                />
              </GridItem>
              <GridItem xs={12} sm={6} lg={9}>
                {state.variant.superVariantBeta && (
                  <GridContainer className={classes.minMaxValueWrapper}>
                    <GridItem>
                      <FormTextInput
                        label={`${t('form.field.minMealTypes')}*`}
                        name="minMealTypes"
                        type="number"
                        value={state.variant.minMealTypes}
                        handleChange={handleChangeIntField}
                        inputSize={12}
                        maxLength={6}
                        inputProps={{ min: 0 }}
                      />
                    </GridItem>
                    <GridItem>
                      <FormTextInput
                        label={`${t('form.field.minPrice')}*`}
                        name="minPrice"
                        type="number"
                        value={state.variant.minPrice}
                        handleChange={handleChangeIntField}
                        inputSize={12}
                        maxLength={6}
                        inputProps={{ min: 0 }}
                      />
                    </GridItem>
                  </GridContainer>
                )}
              </GridItem>
            </GridContainer>
            {state.variant.superVariantBeta && (
              <>
                <GridContainer>
                  <GridItem xs={12} className={classes.valueConfiguration}>
                    <h3>{t('form.field.variant.valueConfiguration')}</h3>
                  </GridItem>
                </GridContainer>
                <GridContainer>
                  <GridItem xs={12}>
                    <SuperVariantBetaPricesTable
                      multinational={multinational}
                      key={`svbpt-${(state.variant.calories ?? []).length}`}
                      state={state}
                      setState={setState}
                    />
                  </GridItem>
                </GridContainer>
              </>
            )}
          </CardBody>
        </Card>
      </div>
      <div className={classes.formControlButton}>
        <FormControlButtons
          discardText={t('form.cancel')}
          submitText={t('form.save')}
          cancelPath="/admin/variants"
          handleSubmit={handleSubmit}
        />
      </div>
    </AdminTable>
  );
};

export default FormVariantsView;
